/**
 * Currency formatting utilities for consistent display across the application
 * 
 * Currency: Brazilian Real (BRL)
 * Exchange rate used for migration: 1 USD = 5.0 BRL (approximate)
 */

/**
 * Format subscription value as Brazilian Real format
 * @param value - Value in reais (e.g., 15.90)
 * @returns Formatted currency string (e.g., "R$ 15,90")
 */
export const formatSubscriptionValue = (value: number = 15.90): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

/**
 * Format currency value with locale-specific formatting
 * @param value - Value in reais
 * @param locale - Locale for formatting (default: 'pt-BR')
 * @param currency - Currency code (default: 'BRL')
 * @returns Formatted currency string using Intl.NumberFormat
 */
export const formatCurrency = (
  value: number,
  locale: string = 'pt-BR',
  currency: string = 'BRL'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
  }).format(value);
};

/**
 * Parse user input to float value
 * @param input - User input as string (e.g., "15,90" or "R$ 15,90" or "15.90")
 * @returns Value in reais
 */
export const parseToFloat = (input: string): number => {
  // Remove currency symbols, spaces, and handle Brazilian format
  let cleanInput = input.replace(/[R$\s]/g, '');
  
  // Handle Brazilian format: replace comma with dot for parsing
  // But only if there's no dot after the comma (to avoid issues with thousands separator)
  if (cleanInput.includes(',') && !cleanInput.match(/,\d{3}/)) {
    cleanInput = cleanInput.replace(',', '.');
  }
  
  const value = parseFloat(cleanInput);
  return isNaN(value) ? 0.0 : value;
};

/**
 * Format float value to display value for input fields (Brazilian format)
 * @param value - Value in reais
 * @returns Formatted string for input (e.g., "79,90")
 */
export const formatFloatForInput = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

/**
 * Format value for display while typing (Brazilian format)
 * @param input - Current input string
 * @returns Formatted input string
 */
export const formatBRLInput = (input: string): string => {
  // Remove all non-numeric characters except comma and dot
  let cleaned = input.replace(/[^\d,.]/g, '');
  
  // Handle multiple commas or dots
  const parts = cleaned.split(/[,.]/);
  if (parts.length > 2) {
    cleaned = parts[0] + ',' + parts[1];
  }
  
  // Ensure only 2 decimal places after comma
  if (cleaned.includes(',')) {
    const [integer, decimal] = cleaned.split(',');
    cleaned = integer + ',' + (decimal ? decimal.substring(0, 2) : '');
  }
  
  return cleaned;
};